import { api } from '../client';

export type ShipmentStatus =
  'DRAFT' | 'BOOKED' | 'IN_TRANSIT' | 'DELIVERED' | 'DELAYED' | 'CANCELLED';

export interface ShipmentItem {
  id: string;
  shipmentId: string;
  itemId: string;
  item?: { id: string; sku: string; name: string; unit: string; unitCost?: number };
  quantity: number;
}

export interface ShipmentMilestone {
  id: string;
  shipmentId: string;
  milestone: string;
  description?: string;
  location?: string;
  timestamp: string;
}

export interface Shipment {
  id: string;
  shipmentNumber: string;
  companyId: string;
  company?: { id: string; name: string; displayName: string };
  originId: string;
  origin?: {
    id: string;
    name: string;
    code: string;
    type: string;
    address?: Record<string, unknown>;
  };
  destinationId: string;
  destination?: {
    id: string;
    name: string;
    code: string;
    type: string;
    address?: Record<string, unknown>;
  };
  status: ShipmentStatus;
  carrier?: string;
  carrierId?: string | null;
  trackingNumber?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  totalWeight?: number;
  totalVolume?: number;
  items?: ShipmentItem[];
  milestones?: ShipmentMilestone[];
  _count?: { items: number; milestones: number; poLinks?: number };
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentWithOrders extends Shipment {
  purchaseOrders: Array<{
    id: string;
    poNumber: string;
    supplier: { id: string; name: string; code: string };
    orderDate: string;
    totalAmount: number;
    currency: string;
    status: string;
    items: Array<{
      id: string;
      itemId: string;
      item: { sku: string; name: string; unit: string };
      quantity: number;
      unitPrice: number;
      receivedQty: number;
      shippedQty: number;
      quantityInThisShipment: number;
    }>;
  }>;
}

export interface CreateShipmentDto {
  companyId: string;
  originId: string;
  destinationId: string;
  carrier?: string;
  carrierId?: string | null;
  trackingNumber?: string;
  estimatedDeparture?: string;
  estimatedArrival?: string;
  totalWeight?: number;
  totalVolume?: number;
  items?: { itemId: string; quantity: number }[];
  purchaseOrderId?: string;
}

export interface QueryShipmentParams {
  search?: string;
  status?: ShipmentStatus;
  originId?: string;
  destinationId?: string;
  companyId?: string;
  page?: number;
  limit?: number;
}

export interface UpdateStatusDto {
  status: ShipmentStatus;
  milestoneDescription?: string;
  location?: string;
  timestamp?: string;
}

export const shipmentsApi = {
  async getAll(params?: QueryShipmentParams) {
    const response = await api.get<{
      data: Shipment[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/shipments', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<Shipment>(`/shipments/${id}`);
    return response.data;
  },

  async getByTracking(trackingNumber: string) {
    const response = await api.get<Shipment>(`/shipments/tracking/${trackingNumber}`);
    return response.data;
  },

  async getMilestones(id: string) {
    const response = await api.get<ShipmentMilestone[]>(`/shipments/${id}/milestones`);
    return response.data;
  },

  async create(data: CreateShipmentDto) {
    const response = await api.post<Shipment>('/shipments', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateShipmentDto>) {
    const response = await api.patch<Shipment>(`/shipments/${id}`, data);
    return response.data;
  },

  async updateStatus(id: string, data: UpdateStatusDto) {
    const response = await api.patch<Shipment>(`/shipments/${id}/status`, data);
    return response.data;
  },

  async addMilestone(
    id: string,
    data: { milestone: string; description?: string; location?: string; timestamp?: string }
  ) {
    const response = await api.post<Shipment>(`/shipments/${id}/milestones`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete(`/shipments/${id}`);
    return response.data;
  },

  async getShipmentWithOrders(id: string) {
    const response = await api.get<ShipmentWithOrders>(`/shipments/${id}/with-orders`);
    return response.data;
  },
};
